/**
 * Shared Configuration for Customer List Components
 * Common column renderers, filter factories, and utilities
 */
import type { FilterItemConfig } from '@/components/ui/data-table-customized'
import { DEPARTMENT_IDS } from '@/constants/departments'
import {
    CUSTOMER_SOURCES,
    CUSTOMER_SOURCE_TYPES,
    INDUSTRIES,
} from '@/modules/Customer/constants'
import {
    getCsPoolDepartmentIds,
    getEmployeeContains,
    getEmployeesDefaultOptions,
    getEmployeesOptionsPaginated,
} from '@/modules/Employee/services'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { generatePathDatacentral, paths } from '@/utils/path'
import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'

// Default department IDs for employee filters
const EMPLOYEE_FILTER_DEPT_IDS = [
    DEPARTMENT_IDS.ACCOUNT,
    DEPARTMENT_IDS.SALES,
    DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
]

// ============================================================================
// Shared Column Renderers
// ============================================================================

/** Render employee names list with italic fallback */
export const renderEmployeeNames = (value: any[], fallbackText: string) => {
    const names = value?.map((item: any) => item?.employee_name)?.join(', ')
    if (!names)
        return <span className="text-gray-400 italic">{fallbackText}</span>
    return (
        <span className="truncate block" title={names}>
            {names}
        </span>
    )
}

/** Render industry with unknown fallback */
export const renderIndustry = (value: string, unknownText: string) => {
    if (!value || value === '-1')
        return <span className="text-destructive italic">{unknownText}</span>
    return <span className="capitalize">{value}</span>
}

/** Customer name link renderer */
export const renderCustomerLink = (customerId: number, name: string) => (
    <Link
        to={generatePathDatacentral(paths.customerDetail, { id: customerId })}
        className="text-primary hover:underline uppercase"
    >
        {customerId} - {name}
    </Link>
)

/** Service status chip color map — shared across active/trial/cancelled */
export const SERVICE_STATUS_CHIP_COLORS = (t: TFunction) => ({
    active: { label: t('service-status.active'), color: 'success' as const },
    trialing: { label: t('service-status.trialing'), color: 'info' as const },
    cancelled: {
        label: t('service-status.cancelled'),
        color: 'error' as const,
    },
    rejected: { label: t('service-status.rejected'), color: 'error' as const },
    'pending-terminated': {
        label: t('service-status.pending-terminated'),
        color: 'warning' as const,
    },
    'waiting-confirm': {
        label: t('service-status.waiting-confirm'),
        color: 'warning' as const,
    },
    'waiting-create-service': {
        label: t('service-status.waiting-create-service'),
        color: 'warning' as const,
    },
    'on-resubmit-information': {
        label: t('service-status.on-resubmit-information'),
        color: 'warning' as const,
    },
})

// ============================================================================
// Shared Filter Item Factories
// ============================================================================

/** Paginated employee options fetcher (sale: ACCOUNT + SALES + BOD) */
const salePaginatedOptions = (query: string, page?: number) =>
    getEmployeesOptionsPaginated(
        query,
        EMPLOYEE_FILTER_DEPT_IDS,
        undefined,
        page
    )

/** Paginated employee options fetcher (CS pool: ACCOUNT + Customer Service) */
const csPaginatedOptions = async (query: string, page?: number) => {
    const departmentIds = await getCsPoolDepartmentIds()
    return getEmployeesOptionsPaginated(query, departmentIds, undefined, page)
}

/** Overridable fields for employee filter factories */
type EmployeeFilterOverrides = {
    label?: string
    getDefaultOptions?: (ids: any[]) => Promise<any[]>
    includeSpecialOptions?: boolean
}

/** Sale followers async-select filter */
export const createSaleFollowersFilter = (
    t: TFunction,
    overrides?: EmployeeFilterOverrides
): FilterItemConfig => ({
    name: 'customer_sale_followers',
    type: 'async-select' as const,
    label: overrides?.label ?? t('customer_sales_followers'),
    placeholder: t('select_customer_sale_followers'),
    asyncOptions: salePaginatedOptions,
    getDefaultOptions:
        overrides?.getDefaultOptions ?? getEmployeesDefaultOptions,
    multiple: true,
})

/** CS followers async-select filter (ACCOUNT department only, with no-assignee/off options) */
export const createCsFollowersFilter = (
    t: TFunction,
    overrides?: EmployeeFilterOverrides
): FilterItemConfig => ({
    name: 'customer_cs_followers',
    type: 'async-select' as const,
    label: overrides?.label ?? t('customer_cs_followers'),
    placeholder: t('select_customer_cs_followers'),
    asyncOptions: async (query, page) => {
        const result = await csPaginatedOptions(query, page)
        if (
            overrides?.includeSpecialOptions !== false &&
            page === 1 &&
            !query
        ) {
            return {
                ...result,
                options: [
                    { label: t('no-cs-assignee'), value: '-1' },
                    { label: t('cs-assignee-off'), value: '-2' },
                    ...result.options,
                ],
            }
        }
        return result
    },
    getDefaultOptions:
        overrides?.getDefaultOptions ?? getEmployeesDefaultOptions,
    multiple: true,
})

/** Industry multi-select filter */
export const createIndustryFilter = (t: TFunction): FilterItemConfig => ({
    name: 'customer_industry',
    type: 'multi-select',
    label: t('customer_industry'),
    placeholder: t('select_customer_industry'),
    options: INDUSTRIES.map((i) => ({ label: i.label, value: i.value })),
})

/** Product group async-select filter */
export const createProductGroupFilter = (
    t: TFunction,
    name = 'product_group_name'
): FilterItemConfig => ({
    name,
    type: 'async-select',
    label: t('product_group'),
    placeholder: t('select_' + name),
    asyncOptions: getProductsGroupOptions,
    multiple: true,
})

/** Customer source + source_type filters (returns 2 items) */
export const createSourceFilters = (t: TFunction): FilterItemConfig[] => [
    {
        name: 'customer_source',
        type: 'multi-select',
        label: t('customer_source'),
        placeholder: t('select_customer_source'),
        options: CUSTOMER_SOURCES(t),
    },
    {
        name: 'customer_source_type',
        type: 'multi-select',
        label: t('customer_source_type'),
        placeholder: t('select_customer_source_type'),
        options: CUSTOMER_SOURCE_TYPES(t),
    },
]

// ============================================================================
// Composed Filter Sets
// ============================================================================

/** Common filters for journey-based lists (CoolLead, QualifiedProspects, DeadLead) */
export const getCommonCustomerFilters = (t: TFunction): FilterItemConfig[] => [
    createSaleFollowersFilter(t, {
        label: t('employee'),
        getDefaultOptions: (values: number[]) =>
            getEmployeeContains({ employee_ids: values }),
    }),
    createIndustryFilter(t),
    {
        name: 'customer_service_interestings',
        type: 'async-select',
        label: t('customer_service_interestings'),
        placeholder: t('select_customer_service_interestings'),
        asyncOptions: getProductsGroupOptions,
        multiple: true,
    },
    {
        name: 'customer_created_at',
        type: 'daterangepicker',
        names: ['customer_created_at', 'customer_created_at_to'],
        label: t('created_at'),
        placeholder: t('select_created_at'),
        showPresets: true,
    },
    ...createSourceFilters(t),
]

/** Filters for service-based lists (Trial, Cancelled) */
export const getServiceListFilters = (t: TFunction): FilterItemConfig[] => [
    createSaleFollowersFilter(t, { label: t('sale_followers') }),
    createCsFollowersFilter(t, { label: t('cs_followers') }),
    createIndustryFilter(t),
    createProductGroupFilter(t),
]

// ============================================================================
// Filter Defaults
// ============================================================================

/** Filter defaults for journey-based lists */
export const JOURNEY_FILTER_DEFAULTS = {
    customer_sale_followers: [],
    customer_industry: [],
    customer_service_interestings: [],
    customer_created_at: [],
    customer_source: [],
    customer_source_type: [],
}

/** Filter defaults for service-based lists */
export const SERVICE_FILTER_DEFAULTS = {
    customer_sale_followers: [],
    customer_cs_followers: [],
    customer_industry: [],
    product_group_name: [],
    service_start_date: [],
}

// ============================================================================
// Date Utilities
// ============================================================================

export const formatDateRange = (startDate: string, endDate: string) => {
    const start = startDate ? dayjs(startDate).format('DD/MM/YYYY') : '-'
    const end = endDate ? dayjs(endDate).format('DD/MM/YYYY') : '-'
    return `${start} → ${end}`
}
